import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Monitor, Smartphone,
  RotateCw, VolumeX, Volume2, WifiOff,
  Maximize2, Minimize2,
  Video, VideoOff,
} from 'lucide-react'
import { Button } from '@meno/ui/components/ui/button'
import { Switch } from '@meno/ui/components/ui/switch'
import OfflineAlertModal, { describeAlertRule } from '@/components/OfflineAlertModal'
import { SettingCard, SettingOption, SECTION_STACK } from './settings-primitives'
import type { OrientationType, RotationDegrees, ContentScaling } from './editBufferReducer'

type ScreenLike = {
  id: number
  public_id: string
  status: string
  // Legacy rows predate the column and come back undefined; the backend treats
  // those as Android, so only an explicit 'web' opts into the web-player card.
  device_kind?: 'android' | 'web'
}

export interface SettingsColumnProps {
  screen:                 ScreenLike
  // Two-axis orientation — `orientationType` is the content aspect ratio
  // (horizontal = 16:9, vertical = 9:16); `rotationDegrees` rotates the
  // output around centre at 0 / 90 / 180 / 270. The combination decides
  // both content framing and physical-mount orientation.
  orientationType:        OrientationType
  setOrientationType:     (v: OrientationType) => void
  rotationDegrees:        RotationDegrees
  setRotationDegrees:     (v: RotationDegrees) => void
  audioMode:              'muted' | 'unmute'
  setAudioMode:           (v: 'muted' | 'unmute') => void
  // Independent toggle to mute the video content's own audio track.
  videoMuted:             boolean
  setVideoMuted:          (v: boolean) => void
  // Web-player-only chrome toggle. Android has no browser chrome to hide, so
  // the card it drives is rendered only for device_kind === 'web'.
  hidePlayerControls:     boolean
  setHidePlayerControls:  (v: boolean) => void
  // Master audio level 0–100, shown/edited only while the screen is unmuted.
  volume:                 number
  setVolume:              (v: number) => void
  // Content fit — how each item is framed against the screen (contain vs cover).
  scaling:                ContentScaling
  setScaling:             (v: ContentScaling) => void
  offlineAlertMinutes:    number | null
  setOfflineAlertMinutes: (v: number | null) => void
}

const ROTATIONS: RotationDegrees[] = [0, 90, 180, 270]

export default function SettingsColumn({
  screen,
  orientationType, setOrientationType,
  rotationDegrees, setRotationDegrees,
  audioMode, setAudioMode,
  videoMuted, setVideoMuted,
  hidePlayerControls, setHidePlayerControls,
  volume, setVolume,
  scaling, setScaling,
  offlineAlertMinutes, setOfflineAlertMinutes,
}: SettingsColumnProps) {
  const { t } = useTranslation()
  const [wifiModalOpen,       setWifiModalOpen]       = useState(false)

  return (
    <>
      {/* space-y-7: sections are their own cards on the page background now,
          so the gap between them is the only thing grouping their contents.
          At space-y-5 (the old in-panel rhythm) a section's eyebrow sat almost
          as close to the card above it as to its own. */}
      <div className={SECTION_STACK}>
        {/* ── View — orientation, rotation, and content fit merged into one
            section, each keeping its own subtitle. Orientation = content aspect
            (16:9 / 9:16); Rotation = quarter-turn the output to match the mount;
            Content fit = contain vs cover framing. ── */}
        <SettingCard title={t('screens.viewSection', 'View')}>
          <div className="space-y-5">
            {/* Orientation */}
            <div className="space-y-3">
              <h4 className="text-sm font-semibold">{t('screens.orientation', 'Orientation')}</h4>
              <div className="grid grid-cols-2 gap-3">
                <SettingOption
                  active={orientationType === 'horizontal'}
                  onClick={() => setOrientationType('horizontal')}
                  icon={Monitor}
                  label={t('screens.orientHorizontal', 'Horizontal')}
                />
                <SettingOption
                  active={orientationType === 'vertical'}
                  onClick={() => setOrientationType('vertical')}
                  icon={Smartphone}
                  label={t('screens.orientVertical', 'Vertical')}
                />
              </div>
            </div>

            {/* Rotation */}
            <div className="space-y-3">
              <h4 className="text-sm font-semibold">{t('screens.rotation', 'Rotation')}</h4>
              <div className="grid grid-cols-4 gap-3">
                {ROTATIONS.map(deg => (
                  <SettingOption
                    key={deg}
                    active={rotationDegrees === deg}
                    onClick={() => setRotationDegrees(deg)}
                    icon={RotateCw}
                    iconStyle={{ transform: `rotate(${deg}deg)` }}
                    label={`${deg}°`}
                  />
                ))}
              </div>
            </div>

            {/* Content fit */}
            <div className="space-y-3">
              <h4 className="text-sm font-semibold">{t('screens.scaling', 'Content fit')}</h4>
              <div className="grid grid-cols-2 gap-3">
                <SettingOption
                  active={scaling === 'default'}
                  onClick={() => setScaling('default')}
                  icon={Minimize2}
                  label={t('screens.scalingFit', 'Fit')}
                />
                <SettingOption
                  active={scaling === 'fit'}
                  onClick={() => setScaling('fit')}
                  icon={Maximize2}
                  label={t('screens.scalingFill', 'Fill')}
                />
              </div>
              <p className="text-xs text-muted-foreground">
                {t('screens.scalingHint', 'Fit keeps the whole item visible (adding bars if needed). Fill makes content cover the screen, cropping the edges.')}
              </p>
            </div>
          </div>
        </SettingCard>

        {/* Sound — the master audio switch, then video sound UNDER it.
            Both players compute video audio as
            `muted = !audio_unmuted || video_muted || ...`, so the video switch
            has never been independent: with the master muted, turning video
            sound back on changes nothing on the wall. The card used to print
            the two switches as peers with the dependent one FIRST, so muting
            everything and then re-enabling "Video sound on" read as a fix and
            silently wasn't. Master now leads, and the video row goes inert and
            says why while the screen is muted. */}
        <SettingCard title={t('screens.sound', 'Sound')}>
          {(() => {
            const on = audioMode === 'unmute'
            const videoOn = !videoMuted
            return (
              <>
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2">
                    {on ? <Volume2 className="w-4 h-4 text-muted-foreground" /> : <VolumeX className="w-4 h-4 text-muted-foreground" />}
                    <span className="text-sm font-medium">
                      {on ? t('screens.audioOn', 'Audio on') : t('screens.audioMuted', 'Muted')}
                    </span>
                  </div>
                  <Switch
                    checked={on}
                    onCheckedChange={v => setAudioMode(v ? 'unmute' : 'muted')}
                    className="data-[state=checked]:bg-primary"
                  />
                </div>

                <div className="border-t border-[hsl(var(--border-control))]" />

                {/* Video's own audio track — only reachable while the screen
                    is unmuted, because that is the only case where it has any
                    effect. */}
                <div className={`flex items-center justify-between gap-3 ${on ? '' : 'opacity-50'}`}>
                  <div className="flex items-center gap-2">
                    {videoOn ? <Video className="w-4 h-4 text-muted-foreground" /> : <VideoOff className="w-4 h-4 text-muted-foreground" />}
                    <div>
                      <span className="text-sm font-medium block">
                        {videoOn ? t('screens.videoSoundOn', 'Video sound on') : t('screens.videoMuted', 'Video muted')}
                      </span>
                      {!on && (
                        <span className="text-xs text-muted-foreground">
                          {t('screens.videoSoundNeedsAudio', 'Turn audio on first — the screen is muted.')}
                        </span>
                      )}
                    </div>
                  </div>
                  <Switch
                    checked={videoOn}
                    disabled={!on}
                    onCheckedChange={v => setVideoMuted(!v)}
                    className="data-[state=checked]:bg-primary"
                  />
                </div>

                {on && (
                  <div className="flex items-center gap-3">
                    <input
                      type="range"
                      min={0}
                      max={100}
                      value={volume}
                      onChange={e => setVolume(Number(e.target.value))}
                      aria-label={t('screens.volume', 'Volume')}
                      className="flex-1 range-fill"
                      style={{ '--range-fill': `${volume}%` } as React.CSSProperties}
                    />
                    <div className="flex items-center gap-1 shrink-0">
                      <input
                        type="number"
                        min={0}
                        max={100}
                        value={volume}
                        onChange={e => setVolume(Number(e.target.value))}
                        aria-label={t('screens.volumePercent', 'Volume percentage')}
                        className="w-14 h-9 rounded-lg border border-border bg-background px-2 text-sm text-center tabular-nums focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary/30 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-inner-spin-button]:m-0"
                      />
                      <span className="text-sm text-muted-foreground">%</span>
                    </div>
                  </div>
                )}
              </>
            )
          })()}
        </SettingCard>

        {/* ── Status — ping (locate this screen) + offline Wi-Fi alerts merged
            into one section; each keeps its icon + description. ── */}
        {/* Web player — only meaningful for browser screens. An Android TV has
            no browser chrome to hide, so showing this there would be a setting
            that silently does nothing. */}
        {screen.device_kind === 'web' && (
          <SettingCard title={t('screens.webPlayerSection', 'Web player')}>
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <Maximize2 className="w-4 h-4 text-muted-foreground" />
                <div>
                  <span className="text-sm font-medium block">
                    {t('screens.hidePlayerControls', 'Hide fullscreen prompt')}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    {t('screens.hidePlayerControlsHint', 'Hides the on-screen fullscreen and install buttons, and the fullscreen row in the player menu.')}
                  </span>
                </div>
              </div>
              <Switch
                checked={hidePlayerControls}
                onCheckedChange={setHidePlayerControls}
                className="data-[state=checked]:bg-primary"
              />
            </div>
            {hidePlayerControls && (
              <p className="text-xs text-muted-foreground border-t border-border pt-3">
                {t('screens.hidePlayerControlsWarn', 'That prompt is how a browser enters fullscreen. Only hide it on screens already launched fullscreen or installed as an app.')}
              </p>
            )}
          </SettingCard>
        )}

        <SettingCard title={t('screens.monitoringSection', 'Playback monitoring')}>
          <div className="space-y-5">
            <div className="space-y-3">
              <div className="flex items-start gap-4">
                <div className="w-14 h-14 rounded-xl bg-muted/40 border border-border flex items-center justify-center shrink-0">
                  <WifiOff className="w-6 h-6 text-muted-foreground" />
                </div>
                <p className="text-sm text-muted-foreground leading-relaxed pt-1">
                  {t('screens.wifiNotifyDesc', 'Get notified when this screen has been offline for a while. Pick a preset or build a custom rule (e.g. alert after 2 days, or after 4 hours).')}
                </p>
              </div>
              <Button
                variant="outline"
                onClick={() => setWifiModalOpen(true)}
                className="w-full justify-between"
              >
                <span>
                  {offlineAlertMinutes === null
                    ? t('screens.wifiNever', 'Never')
                    : describeAlertRule(offlineAlertMinutes, t)}
                </span>
                <span className="text-xs text-muted-foreground">
                  {t('common.change', 'Change')}
                </span>
              </Button>
            </div>
          </div>
        </SettingCard>

      </div>


      {/* Offline-alert rule picker — preset pills + custom (weekly/daily). */}
      <OfflineAlertModal
        open={wifiModalOpen}
        value={offlineAlertMinutes}
        onClose={() => setWifiModalOpen(false)}
        onSave={(m) => setOfflineAlertMinutes(m)}
      />
    </>
  )
}
